import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";

function IncomeChart({
  userId,
  // override these if you want different colors later
  colors = {
    instagramPost: "#7C3AED", // purple-600
    instagramReel: "#10B981", // emerald-500
    youtubeShort: "#F59E0B", // amber-500
    spotifyPodcast: "#EF4444", // red-500
    grid: "#E5E7EB", // gray-200
    axis: "#9CA3AF", // gray-400
  },
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const lastSixMonths = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        name: d.toLocaleString("default", { month: "short" }),
        fullDate: d.toISOString().slice(0, 7), // YYYY-MM
      });
    }
    return months;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        setData(null);

        const url = `${
          import.meta.env.VITE_BACKEND_URL
        }/dash/dashboard-insights/social-media-insights`;
        const res = await axios.get(url, { params: { user_id: userId } });

        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Failed to fetch insights.");
        }

        const base = lastSixMonths.map((m) => ({
          name: m.name,
          fullDate: m.fullDate,
          instagramPost: 0,
          instagramReel: 0,
          youtubeShort: 0,
          spotifyPodcast: 0,
        }));

        const merged = base.map((m) => {
          const row = res.data.data?.[m.fullDate] || {};
          return {
            ...m,
            instagramPost: Number(row["1"] || 0),
            instagramReel: Number(row["2"] || 0),
            youtubeShort: Number(row["3"] || 0),
            spotifyPodcast: Number(row["4"] || 0),
          };
        });

        if (!cancelled) setData(merged);
      } catch (e) {
        if (!cancelled)
          setError(e.message || "An error occurred while fetching data.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, lastSixMonths]);

  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!data) return <div>Loading data...</div>;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
        <XAxis dataKey="name" stroke={colors.axis} />
        <YAxis stroke={colors.axis} />
        <Tooltip
          contentStyle={{ borderRadius: 8 }}
          cursor={{ strokeDasharray: "3 3" }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="instagramPost"
          name="Instagram Post"
          stroke={colors.instagramPost}
          dot={{ r: 3 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="instagramReel"
          name="Instagram Reel"
          stroke={colors.instagramReel}
          dot={{ r: 3 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="youtubeShort"
          name="YouTube Short"
          stroke={colors.youtubeShort}
          dot={{ r: 3 }}
          activeDot={{ r: 6 }}
        />
        <Line
          type="monotone"
          dataKey="spotifyPodcast"
          name="Spotify Podcast"
          stroke={colors.spotifyPodcast}
          dot={{ r: 3 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default IncomeChart;
